import { Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FilterBaseAccessService } from '../../auth/filterbase-access';
import { CommonModule } from '@angular/common';
import { ProfileApiService } from './profile-api.service';
import { Store } from '@ngrx/store';
import { AuthState } from '../../auth/store/auth.model';
import { selectUser } from '../../auth/store/auth.selector';
import { take } from 'rxjs';
import { ToasterService } from '../../providers/toaster/toaster.service';
import { Roles } from '../../providers/static-data';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [RouterModule , CommonModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.css'
})
export class ProfileComponent {

  backGroundImage: string = 'https://images.unsplash.com/photo-1618556450994-a6a128ef0d9d?q=80&w=1964&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
  profile: string = 'https://media.istockphoto.com/id/2151669184/vector/vector-flat-illustration-in-grayscale-avatar-user-profile-person-icon-gender-neutral.jpg?s=612x612&w=0&k=20&c=UEa7oHoOL30ynvmJzSCIPrwwopJdfqzBs0q69ezQoM8='
  profileImageUrl: string = this.profile;
  profileData: any = null;

  private isUpdatingProfileImage = false;
  displayName: string = 'User';
  roleLabel: string = '';
  emailText: string = '-';
  mobileText: string = '-';
  currenTab: string = 'person'
  isprofileaccess: boolean=false;
  tabs = [
    { name: 'Personal Details', value: 'person', url: 'person-details' },
    { name: 'Address Details', value: 'address', url: 'address' },
    { name: 'Parent Details', value: 'parent', url: 'parent-details' },
    { name: 'Education', value: 'education', url: 'education' },
    { name: 'Co-curricular', value: 'co-curricular', url: 'co-curricular' },
    { name: 'Work Experience', value: 'work', url: 'work-experience' },
  ]
  
  private readonly profileNameUpdatedHandler = (event: Event) => {
    const customEvent = event as CustomEvent<{ name?: string }>;
    this.displayName = customEvent.detail?.name || this.getStoredProfileName() || 'User';
  };
  

  constructor(
    access: FilterBaseAccessService,
    private profileApi: ProfileApiService,
    private store: Store<AuthState>,
    private toasterService: ToasterService
  ) {
    this.isprofileaccess=access.checkaccessforfiltersandactions("profile person details");
    
   }
   
  ngOnInit() {
    window.addEventListener('profileNameUpdated', this.profileNameUpdatedHandler);
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        const masterUserId = localStorage.getItem('master_user_id') ?? user?.id ?? this.getUserIdFromStorage();
        if (masterUserId) {
          this.fetchProfile(masterUserId);
        }
      }
    });
   
  }

  ngOnDestroy() {
    window.removeEventListener('profileNameUpdated', this.profileNameUpdatedHandler);
  }

  
  private fetchProfile(userId: number | string) {
    this.profileApi.getUserById(userId).subscribe({
      next: (res: any) => {
        if (res?.status === 'success') {
          this.applyProfileData(res?.data ?? {});
        } else {
          this.toasterService.setToaster('error', 'Error', res?.message ?? 'Failed to load profile.');
        }
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Failed to load profile.');
      }
    });
    this.applyProfileData({})
  }

  
  private applyProfileData(data: any) {
    this.profileData = JSON.parse(localStorage.getItem('auth_data') || '{}');
    this.displayName = data?.name || this.getFullNameFromProfileData(data) || this.getStoredProfileName() || 'User';
    this.roleLabel = data?.role?.name || data?.role_name || this.getRoleLabel(data?.role_id ?? this.profileData?.user?.role_id);
    this.emailText = data?.email || data?.email_id || this.profileData?.user?.email || '-';
    this.mobileText = data?.mobile || '-';
    const image = data?.profile_photo || data?.image || '';
    this.profileImageUrl = this.resolveImageUrl(image) || this.profile;
  }

  private getFullNameFromProfileData(data: any): string {
    return [data?.first_name, data?.middle_name, data?.last_name]
      .map(part => (part ?? '').toString().trim())
      .filter(Boolean)
      .join(' ');
  }

  private getStoredProfileName(): string {
    const user = this.profileData?.user;
    const fullName = [user?.first_name, user?.middle_name, user?.last_name]
      .map(part => (part ?? '').toString().trim())
      .filter(Boolean)
      .join(' ');

    return user?.name || fullName || '';
  }

  private getRoleLabel(roleId: number | string | undefined | null): string {
    if (roleId === undefined || roleId === null || roleId === '') {
      return '';
    }

    const normalizedId = roleId.toString();
    return Roles.find(role => role.value.toString() === normalizedId)?.label || `Role ${normalizedId}`;
  }

  private resolveImageUrl(path: string) {
    if (!path) {
      return '';
    }
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
      return path;
    }
    return `${path.replace(/^\//, '')}`;
  }

  private getUserIdFromStorage() {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }

  onProfileImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // basic validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (file && !allowedTypes.includes(file.type)) {
      this.toasterService.setToaster('error', 'Error', 'Only image files are allowed.');
      input.value = '';
      return;
    }

    if (!file) return;

    const userId = localStorage.getItem('master_user_id') ?? this.getUserIdFromStorage();
    if (!userId) {
      this.toasterService.setToaster('error', 'Error', 'User not found.');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    this.profileImageUrl = previewUrl;

    this.isUpdatingProfileImage = true;

    const formData = new FormData();

    const authData = JSON.parse(localStorage.getItem('auth_data') || '{}');
    const user = authData?.user ?? authData;

    // Split concatenated/mixed full name into first_name and last_name
    // - Prefer user.first_name if it contains multiple parts
    // - Fallback to user.name if present
    const rawName: string = (user?.first_name ?? user?.name ?? '').toString();
    const parts = rawName.trim().split(/\s+/).filter(Boolean);

    const firstName = (user?.first_name ? parts[0] : '').toString() || parts[0] || '';
    const lastName = parts.slice(1).join(' ').toString();

    const emailId = user?.email_id ?? user?.email ?? '';
    const mobileNo = user?.mobile_no ?? user?.mobile ?? '';

    // Always send split values during profile image update
    formData.append('first_name', firstName);
    formData.append('last_name', lastName || user?.last_name || '');
    if (emailId) formData.append('email_id', emailId);
    if (mobileNo) formData.append('mobile_no', mobileNo);
    
    

    // Always send the image file.
    formData.append('profile_photo', file, file.name);

    this.profileApi.updateUserProfile(userId, formData).subscribe({
      next: (res: any) => {
        this.isUpdatingProfileImage = false;
        if (res?.status === 'success' || res?.status === 200) {
          
          const data = res?.data ?? res?.user ?? {};
          const image = data?.profile_photo || data?.image || '';
          if (image) {
            this.profileImageUrl = this.resolveImageUrl(image) || this.profileImageUrl;
          }
          this.toasterService.setToaster('success', 'Success', 'Profile image updated successfully');
        } else {
          this.toasterService.setToaster('error', 'Error', res?.message ?? 'Failed to update profile image');
        }
      },
      error: (err: any) => {
        this.isUpdatingProfileImage = false;
        this.toasterService.setToaster('error', 'Error', 'Failed to update profile image');
        console.error('Update profile image error:', err);
      }
    });
  }

}

